import React, { useState, useEffect } from 'react';
import './ComponentCosts.css';
import API_BASE_URL from '../config';

const ComponentCosts = () => {
  const [componentCosts, setComponentCosts] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state for adding/editing component costs
  const [formData, setFormData] = useState({
    component_id: '',
    cost_per_unit: '',
    freight_cost: '',
    freight_quantity: '',
    last_invoice_date: '',
    other_cost: '',
    other_cost_quantity: ''
  });

  // Fetch component costs from the backend
  const fetchComponentCosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/component-costs`);
      if (!response.ok) {
        throw new Error('Failed to fetch component costs');
      }
      const data = await response.json();
      setComponentCosts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch components from the backend for dropdown
  const fetchComponents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/components`);
      if (!response.ok) {
        throw new Error('Failed to fetch components');
      }
      const data = await response.json();
      setComponents(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchComponentCosts();
    fetchComponents();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      component_id: '',
      cost_per_unit: '',
      freight_cost: '',
      freight_quantity: '',
      last_invoice_date: '',
      other_cost: '',
      other_cost_quantity: ''
    });
    setShowAddForm(false);
    setEditingCost(null);
  };

  const handleAddCost = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/component-costs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add component cost');
      }

      await fetchComponentCosts();
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateCost = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/component-costs/${editingCost.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update component cost');
      }

      await fetchComponentCosts();
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!window.confirm('Are you sure you want to delete this component cost?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/component-costs/${costId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete component cost');
      }

      await fetchComponentCosts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditCost = (cost) => {
    setEditingCost(cost);
    setFormData({
      component_id: cost.component_id,
      cost_per_unit: cost.cost_per_unit.toString(),
      freight_cost: cost.freight_cost.toString(),
      freight_quantity: cost.freight_quantity.toString(),
      last_invoice_date: cost.last_invoice_date || '',
      other_cost: cost.other_cost.toString(),
      other_cost_quantity: cost.other_cost_quantity.toString()
    });
    setShowAddForm(true);
  };

  // Filter component costs based on search term
  const filteredComponentCosts = componentCosts.filter(cost =>
    cost.component_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get components that don't already have costs (for add form)
  const availableComponents = components.filter(comp => 
    !componentCosts.some(cost => cost.component_id === comp.component_id)
  );

  return (
    <div className="component-costs-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Component Cost Management</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Add and edit component cost information</li>
              <li>Track unit costs, freight, and other expenses</li>
              <li>Manage cost data for procurement planning</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="component-costs-header">
          <h1>Component Costs</h1>
          <p>Manage cost information for components including unit costs, freight, and other expenses</p>
        </div>

        <div className="component-costs-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading component cost data...</p>
          </div>
        ) : (
          <>
            <div className="controls-section">
              <div className="search-filters">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search by Component ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className="search-icon">🔍</span>
                </div>
              </div>

              <button 
                className="add-button"
                onClick={() => setShowAddForm(true)}
                disabled={availableComponents.length === 0}
              >
                + Add New Component Cost
              </button>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
              <div className="form-overlay">
                <div className="form-container">
                  <div className="form-header">
                    <h2>{editingCost ? 'Edit Component Cost' : 'Add New Component Cost'}</h2>
                    <button className="close-button" onClick={resetForm}>×</button>
                  </div>
                  
                  <form onSubmit={editingCost ? handleUpdateCost : handleAddCost}>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Component ID *</label>
                        {editingCost ? (
                          <input
                            type="text"
                            name="component_id"
                            value={formData.component_id}
                            disabled
                            className="disabled-input"
                          />
                        ) : (
                          <select
                            name="component_id"
                            value={formData.component_id}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select a component...</option>
                            {availableComponents.map(comp => (
                              <option key={comp.component_id} value={comp.component_id}>
                                {comp.component_id}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Cost Per Unit ($) *</label>
                        <input
                          type="number"
                          name="cost_per_unit"
                          value={formData.cost_per_unit}
                          onChange={handleInputChange}
                          required
                          min="0"
                          step="0.0000000001"
                        />
                      </div>

                      <div className="form-group">
                        <label>Freight Cost ($)</label>
                        <input
                          type="number"
                          name="freight_cost"
                          value={formData.freight_cost}
                          onChange={handleInputChange}
                          min="0"
                          step="0.0001"
                        />
                      </div>

                      <div className="form-group">
                        <label>Freight Quantity</label>
                        <input
                          type="number"
                          name="freight_quantity"
                          value={formData.freight_quantity}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>

                      <div className="form-group">
                        <label>Last Invoice Date</label>
                        <input
                          type="date"
                          name="last_invoice_date"
                          value={formData.last_invoice_date}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Other Cost ($)</label>
                        <input
                          type="number"
                          name="other_cost"
                          value={formData.other_cost}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div className="form-group">
                        <label>Other Cost Quantity</label>
                        <input
                          type="number"
                          name="other_cost_quantity"
                          value={formData.other_cost_quantity}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="form-actions">
                      <button type="button" onClick={resetForm} className="cancel-button">
                        Cancel
                      </button>
                      <button type="submit" className="save-button">
                        {editingCost ? 'Update Component Cost' : 'Add Component Cost'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Component Costs Table */}
            <div className="table-container">
              <table className="component-costs-table">
                <thead>
                  <tr>
                    <th>Component ID</th>
                    <th>Cost Per Unit</th>
                    <th>Freight Cost</th>
                    <th>Freight Qty</th>
                    <th>Last Invoice Date</th>
                    <th>Other Cost</th>
                    <th>Other Cost Qty</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredComponentCosts.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-data">
                        {searchTerm ? 'No component costs match your search criteria' : 'No component costs found'}
                      </td>
                    </tr>
                  ) : (
                    filteredComponentCosts.map(cost => (
                      <tr key={cost.id}>
                        <td className="component-id">{cost.component_id}</td>
                        <td className="cost-per-unit">${cost.cost_per_unit}</td>
                        <td className="freight-cost">${cost.freight_cost}</td>
                        <td className="freight-quantity">{cost.freight_quantity}</td>
                        <td className="last-invoice-date">{cost.last_invoice_date || '-'}</td>
                        <td className="other-cost">${cost.other_cost}</td>
                        <td className="other-cost-quantity">{cost.other_cost_quantity}</td>
                        <td className="actions">
                          <button 
                            className="action-btn edit"
                            onClick={() => handleEditCost(cost)}
                            title="Edit Component Cost"
                          >
                            ✏️
                          </button>
                          <button 
                            className="action-btn delete"
                            onClick={() => handleDeleteCost(cost.id)}
                            title="Delete Component Cost"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="summary">
              <p>Showing {filteredComponentCosts.length} of {componentCosts.length} component costs</p>
              {availableComponents.length === 0 && componentCosts.length > 0 && (
                <p className="info-message">All components have cost data. No more components available to add.</p>
              )}
            </div>
          </>
        )}
      </div>
      </div>

      {error && (
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
    </div>
  );
};

export default ComponentCosts; 